/**
 * Date/Time formatting utility for consistent display across the UI.
 * 
 * Uses Intl.DateTimeFormat with en-GB locale to ensure LTR display
 * and consistent formatting (DD/MM/YYYY HH:mm:ss).
 */

/**
 * Format a date/time value to a consistent string format.
 * 
 * Format: "26/01/2026 13:26:06" (DD/MM/YYYY HH:mm:ss)
 * 
 * @param value - Date object, ISO string, timestamp number, or null/undefined
 * @returns Formatted date/time string or "—" for invalid/null values
 */
export function formatDateTime(value?: Date | string | number | null): string {
  if (!value) {
    return "—";
  }

  let date: Date;

  // Convert various input types to Date object
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === "string") {
    date = new Date(value);
  } else if (typeof value === "number") {
    date = new Date(value);
  } else {
    return "—";
  }

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return "—";
  }

  // Format using Intl.DateTimeFormat with en-GB locale
  // This ensures LTR display and DD/MM/YYYY format
  const formatter = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false, // 24-hour format
  });

  return formatter.format(date);
}

